/**
 * Low-level readers shared by the SVG → HTML converter (`./index`) and its
 * filter translator (`./filters`). Nothing here knows about layers or the
 * shape of the output: these are the primitives both modules need to get a
 * string, a number, or a CSS color out of an SVG attribute.
 *
 * Extracted so `./filters` can stand on its own rather than importing from
 * `./index`, which imports it back.
 */

export interface DomNode {
  type: string;
  children?: DomNode[];
}

export interface DomElement extends DomNode {
  name: string;
  attribs: Record<string, string>;
  children: DomNode[];
}

export function isElement(node: DomNode): node is DomElement {
  return node.type === "tag";
}

export function readStyleProp(style: string, prop: string): string | undefined {
  const re = new RegExp(`(?:^|;)\\s*${prop}\\s*:\\s*([^;]+)`, "i");
  return re.exec(style)?.[1]?.trim();
}

/**
 * Presentation attributes can live on either the dedicated attribute (e.g.
 * `fill="red"`) or inside the `style="..."` shorthand. The SVG cascade gives
 * the style form priority, but for our flat conversion either form should
 * produce the same output — read the attribute first because it's faster and
 * fall back to scanning the style string.
 */
export function readAttrOrStyle(
  el: DomElement,
  prop: string,
): string | undefined {
  const direct = el.attribs[prop];
  if (direct !== undefined && direct !== "") return direct;
  return readStyleProp(el.attribs.style ?? "", prop);
}

/**
 * Parse a coordinate or dimension. When `viewportSize` is provided, percent
 * values resolve against it (e.g. `"50%"` with viewport 1080 → 540). Without
 * a viewport, percents return 0 — we never want to silently emit `NaN`.
 */
export function num(value: string | undefined, viewportSize?: number): number {
  if (!value) return 0;
  const trimmed = value.trim();
  if (trimmed.endsWith("%")) {
    if (viewportSize === undefined) return 0;
    const pct = Number(trimmed.slice(0, -1));
    return Number.isFinite(pct) ? (pct / 100) * viewportSize : 0;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : 0;
}

export function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : Number(n.toFixed(4)).toString();
}

/**
 * Parse a CSS color into `{r, g, b, a}` channels when it's in a numeric format
 * (hex, rgb(), rgba()). Returns null for named colors, hsl(), or unparseable
 * input — callers fall back to `color-mix` for those. We only need this for
 * the alpha-folding path; round-trip through the editor only requires the
 * common-case formats to look right.
 */
export function parseToRgba(
  color: string,
): { r: number; g: number; b: number; a: number } | null {
  const trimmed = color.trim();
  const hex = /^#([0-9a-f]{3,8})$/i.exec(trimmed);
  if (hex) {
    const h = hex[1]!;
    if (h.length === 3 || h.length === 4) {
      const r = Number(`0x${h[0]}${h[0]}`);
      const g = Number(`0x${h[1]}${h[1]}`);
      const b = Number(`0x${h[2]}${h[2]}`);
      const a = h.length === 4 ? Number(`0x${h[3]}${h[3]}`) / 255 : 1;
      return { r, g, b, a };
    }
    if (h.length === 6 || h.length === 8) {
      const r = Number(`0x${h.slice(0, 2)}`);
      const g = Number(`0x${h.slice(2, 4)}`);
      const b = Number(`0x${h.slice(4, 6)}`);
      const a = h.length === 8 ? Number(`0x${h.slice(6, 8)}`) / 255 : 1;
      return { r, g, b, a };
    }
    return null;
  }
  // rgb(R G B) / rgb(R, G, B) / rgba(R, G, B, A) / rgb(R G B / A).
  const rgb =
    /^rgba?\(\s*(\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)(?:\s*[,/]\s*([\d.]+%?))?\s*\)$/i.exec(
      trimmed,
    );
  if (rgb) {
    const r = Math.round(Number(rgb[1]));
    const g = Math.round(Number(rgb[2]));
    const b = Math.round(Number(rgb[3]));
    let a = 1;
    if (rgb[4]) {
      a = rgb[4].endsWith("%")
        ? Number(rgb[4].slice(0, -1)) / 100
        : Number(rgb[4]);
    }
    return { r, g, b, a };
  }
  return null;
}

export function combineColorWithOpacity(
  color: string,
  opacityRaw: string | undefined,
): string {
  if (opacityRaw === undefined || opacityRaw === "") return color;
  const opacity = Number(opacityRaw.trim());
  if (!Number.isFinite(opacity) || opacity >= 1) return color;
  if (opacity <= 0) return "transparent";
  const rgba = parseToRgba(color);
  if (rgba) {
    const alpha = rgba.a * opacity;
    return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${fmt(alpha)})`;
  }
  return `color-mix(in srgb, ${color} ${fmt(opacity * 100)}%, transparent)`;
}
